import React, { useEffect, useMemo, useState } from "react";
import "./InstallPWA.css";

const DEFAULT_PORTAL_NAME = "Portal de Pacientes Huincul";

const getPortalName = () => {
  const storedName = localStorage.getItem("nombreClinica");
  return storedName || DEFAULT_PORTAL_NAME;
};

const getIosDismissKey = (portalName) => {
  const normalizedPortalName = portalName.replace(/[^a-zA-Z0-9]/g, "");
  return `iosModalDismissed${normalizedPortalName}`;
};

const InstallPWA = () => {
  const portalName = useMemo(getPortalName, []);
  const iosDismissKey = useMemo(() => getIosDismissKey(portalName), [portalName]);

  const [showModal, setShowModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isAndroidDevice = /Android/i.test(navigator.userAgent);
    const standaloneMode =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;

    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);
    setIsStandalone(standaloneMode);

    if (standaloneMode) {
      return undefined;
    }

    let fallbackTimer;

    if (isIOSDevice) {
      const hasDismissedIOSModal = localStorage.getItem(iosDismissKey);
      if (!hasDismissedIOSModal) {
        fallbackTimer = window.setTimeout(() => {
          setShowModal(true);
        }, 2000);
      }
    }

    if (isAndroidDevice) {
      fallbackTimer = window.setTimeout(() => {
        setShowModal(true);
      }, 2000);
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShowModal(true);
    };

    const handleInstalled = () => {
      setDeferredPrompt(null);
      setShowModal(false);
      setIsStandalone(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      if (fallbackTimer) {
        window.clearTimeout(fallbackTimer);
      }
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, [iosDismissKey]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowModal(false);
  };

  const handleClose = () => {
    setShowModal(false);

    if (isIOS) {
      localStorage.setItem(iosDismissKey, "true");
    }
  };

  if (isStandalone || !showModal) {
    return null;
  }

  const showIOSInstructions = isIOS;
  const showAndroidManualInstructions = isAndroid && !deferredPrompt;

  return (
    <div className="pwa-modal-overlay" role="presentation">
      <div
        className="pwa-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwa-modal-title"
      >
        <h2 id="pwa-modal-title">
          {deferredPrompt
            ? `Instalar ${portalName}`
            : showAndroidManualInstructions
              ? "Agregar a pantalla principal"
              : `Instala ${portalName}`}
        </h2>

        {showIOSInstructions ? (
          <div className="pwa-instructions">
            <p>Para instalar {portalName} en tu iPhone:</p>
            <ol>
              <li>Toca Compartir en Safari o Chrome.</li>
              <li>Elige Agregar a pantalla de inicio.</li>
              <li>Toca Agregar para confirmar.</li>
            </ol>
          </div>
        ) : showAndroidManualInstructions ? (
          <div className="pwa-instructions">
            <p>Si el navegador no muestra el instalador automático:</p>
            <ol>
              <li>Toca el menú de tres puntos.</li>
              <li>Elige Instalar app o Agregar a pantalla principal.</li>
              <li>Confirma la instalación.</li>
            </ol>
          </div>
        ) : (
          <p className="pwa-copy">
            Instala {portalName} para entrar más rápido desde tu dispositivo.
          </p>
        )}

        <div className="pwa-modal-buttons">
          {deferredPrompt && !isIOS ? (
            <button onClick={handleInstall} className="install-button" type="button">
              Instalar
            </button>
          ) : null}
          <button onClick={handleClose} className="close-button" type="button">
            {isIOS ? "Entendido" : "Ahora no"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
